import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { marked } from 'marked';


const HeroProfile = () => {
    const { isAdmin, token } = useAuth();

    // Default Empty State matching Schema
    const [profile, setProfile] = useState({
        name: 'Hardik Gupta',
        headline: '',
        subHeadline: '',
        introText: '',
        location: '',
        currentStatus: [],
        identityTiles: [],
        philosophy: [],
        learning: [],
        funFact: '',
        imageUrl: ''
    });

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [formState, setFormState] = useState({});

    // Fetch Profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/profile');
                if (res.data) {
                    setProfile(res.data);
                    setFormState(res.data);
                }
            } catch (error) {
                console.error("Profile fetch error:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    // Handlers
    const handleEditToggle = () => {
        setFormState(profile);
        setIsEditing(!isEditing);
    };

    const handleChange = (e) => {
        setFormState({ ...formState, [e.target.name]: e.target.value });
    };

    const handleArrayChange = (e, field) => {
        // Split by newline to save as array
        setFormState({ ...formState, [field]: e.target.value.split('\n') });
    };

    const handleTileChange = (e, index, key) => {
        const newTiles = [...formState.identityTiles];
        newTiles[index][key] = e.target.value;
        setFormState({ ...formState, identityTiles: newTiles });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);
        try {
            const res = await axios.post('/api/upload-image', formData);
            setFormState({ ...formState, imageUrl: res.data.url });
        } catch (error) {
            console.error(error);
            alert("Upload failed");
        }
    };

    const handleSave = async () => {
        try {
            const res = await axios.put('/api/profile', formState, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setProfile(res.data);
            setIsEditing(false);
        } catch (error) {
            alert("Update failed.");
        }
    };

    const displayImage = formState.imageUrl || profile.imageUrl || '';

    return (
        <div className="bento-card hero-card" style={{ fontFamily: '"Crimson Text", serif', color: '#111' }}>
            {/* Admin Controls */}
            {isAdmin && (
                <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                    {isEditing ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button onClick={handleSave} style={btnStyle(true)}>Save</button>
                            <button onClick={handleEditToggle} style={btnStyle(false)}>Cancel</button>
                        </div>
                    ) : (
                        <button onClick={handleEditToggle} style={btnStyle(false)}>Edit</button>
                    )}
                </div>
            )}

            {/* --- HERO SECTION --- */}
            <div className="profile-header" style={{ borderBottom: '1px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                    <div className="avatar-circle" style={{
                        overflow: 'hidden', border: '3px solid #333', width: '240px', height: '240px', flexShrink: 0, position: 'relative'
                    }}>
                        <img src={displayImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        {isEditing && (
                            <label style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.6)',
                                color: 'white', fontSize: '0.6rem', textAlign: 'center', cursor: 'pointer', padding: '2px'
                            }}>
                                Change <input type="file" onChange={handleImageUpload} style={{ display: 'none' }} />
                            </label>
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <input name="name" value={formState.name} onChange={handleChange} style={{ ...inputStyle, fontSize: '1.5rem', fontWeight: 'bold' }} placeholder="Name" />
                                <input name="headline" value={formState.headline} onChange={handleChange} style={inputStyle} placeholder="Headline" />
                                <input name="subHeadline" value={formState.subHeadline} onChange={handleChange} style={inputStyle} placeholder="Sub-headline" />
                                <input name="location" value={formState.location} onChange={handleChange} style={inputStyle} placeholder="Location" />
                            </div>
                        ) : (
                            <>
                                <h1 style={{ margin: 0, fontSize: '2.2rem', fontWeight: 700 }}>{profile.name}</h1>
                                <h2 style={{ margin: '5px 0', fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>{profile.headline}</h2>
                                <p style={{ margin: 0, fontStyle: 'italic', color: '#555' }}>{profile.subHeadline}</p>
                                <p style={{ margin: '5px 0', fontSize: '0.85rem', fontFamily: '"Space Mono", monospace', color: '#666' }}>{profile.location}</p>

                                {/* Social Links Section */}
                                <div className="social-links" style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '20px', fontSize: '1.2rem', alignItems: 'center' }}>
                                    {/* LinkedIn */}
                                    <a href="https://www.linkedin.com/in/hardikdartmouth/" target="_blank" rel="noopener noreferrer" title="LinkedIn" style={{ textDecoration: 'none', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-brands fa-linkedin" style={{ color: '#0077b5', fontSize: '1.4rem' }}></i> <span>LinkedIn</span>
                                    </a>

                                    {/* GitHub */}
                                    <a href="https://github.com/guptahardik" target="_blank" rel="noopener noreferrer" title="GitHub" style={{ textDecoration: 'none', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-brands fa-github" style={{ color: '#333', fontSize: '1.4rem' }}></i> <span>GitHub</span>
                                    </a>

                                    {/* Email */}
                                    <a href="mailto:connect@hardikquity.com" title="Email" style={{ textDecoration: 'none', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-solid fa-envelope" style={{ color: '#ea4335', fontSize: '1.4rem' }}></i> <span>Email</span>
                                    </a>

                                    {/* ORCID */}
                                    <a href="https://orcid.org/0009-0007-2043-5318" target="_blank" rel="noopener noreferrer" title="ORCID" style={{ textDecoration: 'none', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-brands fa-orcid" style={{ color: '#a6ce39', fontSize: '1.4rem' }}></i> <span>ORCID</span>
                                    </a>

                                    {/* Google Scholar */}
                                    <a href="https://scholar.google.com/citations?hl=en&user=qSIkjwoAAAAJ" target="_blank" rel="noopener noreferrer" title="Google Scholar" style={{ textDecoration: 'none', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fa-solid fa-graduation-cap" style={{ color: '#4285f4', fontSize: '1.4rem' }}></i> <span>Scholar</span>
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div style={{ marginBottom: '30px', fontSize: '1.1rem', lineHeight: '1.6' }}>
                {isEditing ? (
                    <textarea name="introText" value={formState.introText} onChange={handleChange} style={{ ...inputStyle, minHeight: '100px' }} placeholder="Intro Text" />
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: marked.parse(profile.introText || '') }} />
                )}
            </div>

            {/* --- WHAT I'M UP TO --- */}
            <h3 style={sectionHeaderStyle}>What I'm Up To</h3>
            {isEditing ? (
                <textarea
                    value={formState.currentStatus ? formState.currentStatus.join('\n') : ''}
                    onChange={(e) => handleArrayChange(e, 'currentStatus')}
                    style={{ ...inputStyle, minHeight: '80px' }}
                    placeholder="Line separated list"
                />
            ) : (
                <ul style={{ paddingLeft: '20px', marginBottom: '30px' }}>
                    {profile.currentStatus && profile.currentStatus.map((item, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: marked.parseInline(item) }} style={{ marginBottom: '8px' }} />
                    ))}
                </ul>
            )}

            {/* --- IDENTITY TILES --- */}
            <h3 style={sectionHeaderStyle}>Identity</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                {(profile.identityTiles || []).map((tile, i) => (
                    <div key={i} style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '4px', background: '#f9f9f9' }}>
                        {isEditing ? (
                            <>
                                <input value={formState.identityTiles[i]?.title} onChange={(e) => handleTileChange(e, i, 'title')} style={{ ...inputStyle, fontWeight: 'bold' }} />
                                <textarea value={formState.identityTiles[i]?.content} onChange={(e) => handleTileChange(e, i, 'content')} style={inputStyle} />
                            </>
                        ) : (
                            <>
                                <h4 style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>{tile.title}</h4>
                                <p style={{ margin: 0, fontSize: '0.9rem' }}>{tile.content}</p>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {/* --- PHILOSOPHY & LEARNING GRID --- */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px', marginBottom: '30px' }}>
                <div>
                    <h3 style={sectionHeaderStyle}>Philosophy</h3>
                    {isEditing ? (
                        <textarea value={formState.philosophy ? formState.philosophy.join('\n') : ''} onChange={(e) => handleArrayChange(e, 'philosophy')} style={{ ...inputStyle, minHeight: '100px' }} />
                    ) : (
                        <ul style={{ paddingLeft: '20px' }}>
                            {profile.philosophy && profile.philosophy.map((item, i) => <li key={i} style={{ marginBottom: '5px' }}>{item}</li>)}
                        </ul>
                    )}
                </div>
                <div>
                    <h3 style={sectionHeaderStyle}>Learning</h3>
                    {isEditing ? (
                        <textarea value={formState.learning ? formState.learning.join('\n') : ''} onChange={(e) => handleArrayChange(e, 'learning')} style={{ ...inputStyle, minHeight: '100px' }} />
                    ) : (
                        <ul style={{ paddingLeft: '20px' }}>
                            {profile.learning && profile.learning.map((item, i) => <li key={i} style={{ marginBottom: '5px' }}>{item}</li>)}
                        </ul>
                    )}
                </div>
            </div>

            {/* --- FUN FACT --- */}
            <div style={{ borderTop: '1px dashed #999', paddingTop: '15px', fontStyle: 'italic', color: '#444' }}>
                <strong>Fun Fact:</strong>
                {isEditing ? (
                    <input name="funFact" value={formState.funFact} onChange={handleChange} style={inputStyle} />
                ) : (
                    <span style={{ marginLeft: '10px' }}>{profile.funFact}</span>
                )}
            </div>
        </div>
    );
};

// Styles
const sectionHeaderStyle = {
    fontFamily: '"Crimson Text", serif',
    borderBottom: '2px solid #111',
    paddingBottom: '5px',
    marginBottom: '10px',
    fontSize: '1.4rem',
    fontWeight: 'bold'
};

const btnStyle = (primary) => ({
    background: primary ? '#111' : '#fff',
    color: primary ? '#fff' : '#111',
    border: '1px solid #111',
    borderRadius: '4px',
    padding: '4px 8px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontFamily: '"Inter", sans-serif'
});

const inputStyle = {
    width: '100%',
    padding: '8px',
    border: '1px dashed #999',
    marginBottom: '5px',
    fontFamily: 'inherit',
    background: '#fcfcfc'
};

export default HeroProfile;
